import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import logger from '../utils/logger';
import { useAPI } from '../contexts/APIContext';
import { isEmbeddedWebView } from '../utils/inAppBrowser';

/**
 * "Watch while you wait" strip, shown at the bottom of the character-generating
 * step in both web2app funnels (/funnel-auth and /funnel-intro).
 *
 * Generation is the funnel's one open-ended wait, and a progress bar is all
 * there is to look at. This offers a real Pixley episode to watch instead —
 * both to hold the visitor through the wait and to show them what they're
 * signing up for.
 *
 * It plays INLINE, in an overlay over the funnel, rather than linking out. The
 * generation request is in flight in this tab; sending the visitor to another
 * page would abandon it, and a new tab risks the browser suspending this one
 * mid-request on mobile.
 *
 * Its own `fwv-` class namespace and inline <style>, so the two funnels can
 * share one implementation without either one's `fa-`/`fi-` styles reaching in.
 */

// "Curious Creek" — the Pixley Original offered during the wait. Storyverse's
// banner now leads with the newest Original series (Storyverse.js); this
// stays on Curious Creek until deliberately changed. Episode one is fetched at
// mount rather than hardcoded.
// "Hello, Watts!" (season 4): episode 1 "The Stairs" is what the explainer
// slide and this strip play. Was Curious Creek (7e845f9b-…) until 10/21.
const SHOW_ID = '50ae118b-7dac-4127-a3ce-469830db017e';

// The show's first playable episode, or null. Also used by the intro funnel's
// story-preview slide, so both screens always show the same episode.
export const fetchFirstEpisode = async (apiClient) => {
  const result = await apiClient.getStoriverseEpisodes(SHOW_ID);
  const list = Array.isArray(result?.data) ? result.data : result?.data?.episodes;
  // Lowest episode_number, so this always offers the start of the story
  return [...(list || [])]
    .filter((e) => e?.file_url)
    .sort((a, b) => (a.episode_number || 0) - (b.episode_number || 0))[0] || null;
};

// Everything it takes for the episode to really stop. Pausing isn't enough on
// its own: a video popped out into Picture-in-Picture (or iOS's own PiP /
// fullscreen player) plays on outside the overlay, and Chrome keeps it
// playing there even after the element has been removed from the page.
// Dropping the source also stops the download — the episode is a large
// progressive mp4 and generation is still running on the same connection.
// Also used by the intro funnel's watch-a-story slide.
export const stopEpisode = (video) => {
  if (!video) return;
  if (document.pictureInPictureElement === video) {
    document.exitPictureInPicture().catch(() => {});
  }
  if (video.webkitPresentationMode && video.webkitPresentationMode !== 'inline') {
    video.webkitSetPresentationMode?.('inline');
  }
  video.pause();
  video.removeAttribute('src');
  video.load();
};

const formatDuration = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const FunnelWaitVideoStrip = ({ onPlay }) => {
  const { apiClient } = useAPI();
  const [episode, setEpisode] = useState(null);
  const [open, setOpen] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (!apiClient) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const first = await fetchFirstEpisode(apiClient);
        if (!cancelled && first) setEpisode(first);
      } catch (error) {
        // Silent: this is a nicety on top of the wait screen. If it can't load,
        // the visitor just sees the progress bar they'd have seen anyway.
        logger.warn('Funnel wait video unavailable:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient]);

  const close = useCallback(() => setOpen(false), []);

  // Stop the episode whenever the overlay goes away: closed by the visitor,
  // or unmounted because their character finished mid-episode and the funnel
  // moved on — a path that never runs close(). The element is captured when
  // the overlay opens, since the ref is already cleared by unmount cleanup.
  useEffect(() => {
    if (!open) return undefined;
    const video = videoRef.current;
    return () => stopEpisode(video);
  }, [open]);

  // Escape closes, matching every other overlay in the app
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!episode) return null;

  const duration = formatDuration(episode.duration);

  return (
    <>
      <FunnelWaitVideoStyles />

      <motion.button
        className="fwv-strip"
        onClick={() => {
          setOpen(true);
          onPlay?.(episode);
        }}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
        whileTap={{ scale: 0.985 }}
        aria-label={`Watch ${episode.name} while you wait`}
      >
        <span className="fwv-thumb">
          {/* Large source PNG, and it sits below the fold of the wait screen —
              let it arrive whenever it arrives. */}
          <img src={episode.thumbnail_url} alt="" loading="lazy" />
          <span className="fwv-play" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </span>
        <span className="fwv-text">
          <small>Watch while you wait</small>
          <strong>{episode.name}</strong>
          {duration && <span className="fwv-meta">{duration} · Pixley Original</span>}
        </span>
        <span className="fwv-chevron" aria-hidden="true">›</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fwv-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={close}
          >
            <motion.div
              className="fwv-modal"
              role="dialog"
              aria-modal="true"
              aria-label={episode.name}
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 8 }}
              transition={{ type: 'spring', stiffness: 320, damping: 28 }}
              onClick={(e) => e.stopPropagation()}
            >
              <video
                ref={videoRef}
                className="fwv-video"
                src={episode.file_url}
                poster={episode.thumbnail_url}
                controls
                autoPlay
                playsInline
                webkit-playsinline=""
                // Never ahead of the click: the file is hundreds of MB and the
                // generation request is sharing this connection.
                preload="none"
              />
              <button className="fwv-close" onClick={close} aria-label="Close">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
              <p className="fwv-note">Your character is still being made — we'll show you when it's ready.</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const FunnelWaitVideoStyles = () => (
  <style>{`
    .fwv-strip {
      display: flex; align-items: center; gap: 13px;
      width: 100%;
      margin-top: 26px;
      padding: 10px 14px 10px 10px;
      background: #FFFFFF;
      border: 1.5px solid #E8EDF5;
      border-radius: 20px;
      box-shadow: 0 3px 0 rgba(11,26,51,0.05), 0 10px 24px rgba(11,26,51,0.07);
      cursor: pointer;
      text-align: left;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      transition: transform 180ms cubic-bezier(.2,.8,.2,1), box-shadow 180ms, border-color 180ms;
    }
    .fwv-strip:hover {
      transform: translateY(-2px);
      border-color: rgba(10,108,255,0.35);
      box-shadow: 0 5px 0 rgba(11,26,51,0.05), 0 14px 30px rgba(11,26,51,0.10);
    }
    .fwv-strip:focus-visible {
      outline: 3px solid rgba(10,108,255,0.4);
      outline-offset: 2px;
    }

    .fwv-thumb {
      position: relative;
      flex-shrink: 0;
      width: 92px; height: 62px;
      border-radius: 13px;
      overflow: hidden;
      background: #EAF3FF;
    }
    .fwv-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .fwv-play {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      color: #FFF;
      background: linear-gradient(180deg, rgba(11,26,51,0.05), rgba(11,26,51,0.35));
    }
    .fwv-play svg {
      filter: drop-shadow(0 2px 6px rgba(11,26,51,0.45));
      transition: transform 180ms cubic-bezier(.2,.8,.2,1);
    }
    .fwv-strip:hover .fwv-play svg { transform: scale(1.22); }

    .fwv-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
    .fwv-text small {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 11px; font-weight: 700;
      letter-spacing: 0.09em; text-transform: uppercase;
      color: #0A6CFF;
    }
    .fwv-text strong {
      font-family: 'Fredoka', 'Inter', sans-serif;
      font-size: 15.5px; font-weight: 600;
      color: #0B1A38;
      /* Episode titles are short, but never let one wrap the card taller */
      white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    }
    .fwv-meta { font-size: 12px; color: #6C7A8F; }
    .fwv-chevron { flex-shrink: 0; font-size: 22px; line-height: 1; color: #A9B6C9; }

    .fwv-overlay {
      position: fixed; inset: 0;
      z-index: 1000;
      display: flex; align-items: center; justify-content: center;
      padding: 20px;
      background: rgba(6,14,30,0.78);
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
    }
    .fwv-modal {
      position: relative;
      width: min(880px, 100%);
    }
    .fwv-video {
      display: block;
      width: 100%;
      max-height: 76vh;
      border-radius: 18px;
      background: #000;
      box-shadow: 0 30px 80px rgba(0,0,0,0.5);
    }
    .fwv-close {
      position: absolute; top: -14px; right: -14px;
      width: 40px; height: 40px;
      display: flex; align-items: center; justify-content: center;
      border: none; border-radius: 50%;
      background: #FFF; color: #0B1A38;
      cursor: pointer;
      box-shadow: 0 6px 18px rgba(0,0,0,0.35);
      -webkit-tap-highlight-color: transparent;
      transition: transform 160ms cubic-bezier(.2,.8,.2,1);
    }
    .fwv-close:hover { transform: scale(1.08); }
    .fwv-close:active { transform: scale(0.94); }
    .fwv-note {
      margin: 14px 0 0;
      text-align: center;
      font-size: 13.5px;
      color: rgba(255,255,255,0.75);
    }

    @media (max-width: 480px) {
      .fwv-strip { margin-top: 20px; gap: 11px; padding: 9px 12px 9px 9px; border-radius: 18px; }
      .fwv-thumb { width: 80px; height: 54px; border-radius: 11px; }
      .fwv-text strong { font-size: 14.5px; }
      .fwv-overlay { padding: 14px; }
      /* Clear of the notch, since the video fills most of the screen here */
      .fwv-close { top: -8px; right: -6px; width: 36px; height: 36px; }
    }
    @media (max-height: 700px) {
      .fwv-strip { margin-top: 16px; }
      .fwv-video { max-height: 68vh; }
    }
    @media (prefers-reduced-motion: reduce) {
      .fwv-strip:hover { transform: none; }
      .fwv-strip:hover .fwv-play svg { transform: none; }
    }
  `}</style>
);

// In TikTok's (and other apps') in-app browser the episode can't play inline —
// the app sends any <video> fullscreen, out of the funnel mid-generation — so
// there the wait screen doesn't offer it
const FunnelWaitVideo = (props) => (isEmbeddedWebView() ? null : <FunnelWaitVideoStrip {...props} />);

export default FunnelWaitVideo;
